import React, { useContext, useEffect, useState } from 'react';

import { Error as ErrorIcon } from '@digital-hig/icon/display-icons';
import Box from '@digital-hig/mui/Box';
import Button from '@digital-hig/mui/Button';
import { CharacterCounter, useCount } from '@digital-hig/mui/CharacterCounter';
import Divider from '@digital-hig/mui/Divider';
import FormControl from '@digital-hig/mui/FormControl';
import FormControlLabel from '@digital-hig/mui/FormControlLabel';
import FormHelperText from '@digital-hig/mui/FormHelperText';
import InputLabel from '@digital-hig/mui/InputLabel';
import Radio from '@digital-hig/mui/Radio';
import RadioGroup from '@digital-hig/mui/RadioGroup';
import Select from '@digital-hig/mui/Select';
import SvgIcon from '@digital-hig/mui/SvgIcon';
import { themeDark } from '@digital-hig/mui/Theme';
import Typography from '@digital-hig/mui/Typography';
import { SelectChangeEvent } from '@mui/material';
import Input from '@mui/material/Input';
import { createTheme, ThemeOptions, ThemeProvider } from '@mui/material/styles';

import {
  useAddCollectionItem,
  useCollectionsList,
  useCreateCollection,
} from '../../../apis/collections/hooks';
import {
  CollectionArticleItem,
  CollectionClassItem,
  CollectionItem,
  Collection,
} from '../../../common/type';
import Spinner from '../../../components/presentational/spinner';
import I18nContext from '../../contexts/I18nContext';
import LoginContext from '../../contexts/LoginContext';
import { ToastContext } from '../../contexts/ToastContext';

export interface AddToPlaylistProps {
  langCode?: string;
  baseURL?: string;
  contentType: 'class' | 'article';
  contentID: string;
  contentTitle: string;
  eventName?: string;
  classType?: string;
  contentImage?: string;
}

interface ContentItem {
  image: string;
  item_key: string;
  item_source: string;
  content_type: string;
  item_title: string;
  position: number;
  class_type: string;
  event_name: string;
  collection_id: string;
}

function AddToPlaylistForm({
  langCode,
  baseURL,
  onClose,
  ...contentProps
}: AddToPlaylistProps & { onClose: () => void }) {
  const { Trans, t, i18n } = useContext(I18nContext);
  const { userProfile } = useContext(LoginContext);
  const [selectedPlaylist, setSelectedPlaylist] = useState('');
  const [isCreateMode, setIsCreateMode] = useState(false);
  const { data: collectionData, isLoading } = useCollectionsList({
    baseURL,
    userId: userProfile?.sub || '',
    enabled: !!userProfile?.sub,
  });
  // filter out the bookmarks collection
  const data = collectionData?.length
    ? collectionData.filter(
        collection => collection.designation !== 'aubookmarks'
      )
    : [];

  const currentLocale = langCode || i18n.language;
  const publicList =
    data?.filter(
      collection => !collection.id.includes(userProfile?.sub || '')
    ) || [];

  const currentList = publicList.find(
    collection => collection.id === selectedPlaylist
  );

  const noExistingPlaylists = isLoading ? true : data?.length === 0;

  useEffect(() => {
    if (isLoading) return;
    if (noExistingPlaylists && !isCreateMode) {
      setIsCreateMode(true);
    }
  }, [noExistingPlaylists, isLoading]);

  const { setErrorToast, setSuccessToast, closeCurrentToast } =
    useContext(ToastContext);

  const {
    mutateAsync: addToExisting,
    isPending: addToExistingLoading,
    reset: addToExistingReset,
  } = useAddCollectionItem({
    id: selectedPlaylist,
    baseURL,
  });

  const {
    mutateAsync: createPlaylist,
    isPending: createPlaylistLoading,
    reset: createPlaylistReset,
  } = useCreateCollection({
    baseURL,
  });

  const isMutating = addToExistingLoading || createPlaylistLoading;

  const titleCounter = useCount(80);
  const descriptionCounter = useCount(500);
  const [playlistIsPublic, setPlaylistIsPublic] = useState<boolean>(false);

  const resetMutationErrors = () => {
    addToExistingReset();
    createPlaylistReset();
    closeCurrentToast();
  };

  const buildNewItem: () => CollectionItem | undefined = () => {
    const {
      contentType,
      contentImage,
      contentTitle,
      contentID,
      classType,
      eventName,
    } = contentProps;
    let newItem: CollectionItem | undefined;

    if (contentType === 'article') {
      newItem = {
        content_type: 'article',
        image: contentImage,
        item_key: contentID,
        item_source: 'AU',
        item_title: contentTitle,
        position: Number(data?.[0]?.collection_items?.length) + 1 || 0,
      } as CollectionArticleItem;
    }
    if (contentType === 'class') {
      newItem = {
        image: contentImage,
        item_key: contentID,
        item_source: 'AU',
        content_type: contentType,
        item_title: contentTitle,
        position: Number(data?.[0]?.collection_items?.length) + 1 || 0,
        class_type: classType,
        event_name: eventName,
      } as CollectionClassItem;
    }

    return newItem;
  };

  /**
   * Checks if an item already exists in any of the user's collections
   * and shows an alert if it does.
   *
   * @param {Object} payload - The payload containing item details to be added
   * @param {Array} collections - Array of user's existing collections
   * @returns {boolean} - Returns true if item was already in a collection, false otherwise
   */
  const checkItemExistsInCollections = (
    payload: ContentItem,
    collections: Collection[]
  ): boolean => {
    // Extract the item key from the payload
    const itemKeyToAdd = payload.item_key;

    // Extract the target collection ID from the payload
    const targetCollectionId = payload.collection_id;

    // Find the target collection
    const targetCollection = collections.find(
      collection => collection.id === targetCollectionId
    );

    // Check if the item already exists in the target collection
    const itemExists = targetCollection?.collection_items.some(
      item => item.item_key === itemKeyToAdd
    );

    if (itemExists) {
      // Alert the user that the item is already in their collection
      return true;
    }

    return false;
  };

  const handleAddExisting = async () => {
    resetMutationErrors();
    const newItem = buildNewItem();

    const payloadNewItem: ContentItem = {
      ...(newItem as Omit<ContentItem, 'collection_id'>),
      collection_id: selectedPlaylist,
    };

    // Check if the item already exists in any of the user's collections
    const itemExists = checkItemExistsInCollections(payloadNewItem, data);
    console.log('itemExists', itemExists);

    if (itemExists) {
      setErrorToast({
        onClose: resetMutationErrors,
        message: t('addToList.itemAlreadyInPlaylist', { ns: 'common' }),
      });
    } else {
      if (typeof newItem === 'undefined') {
        setErrorToast({
          onClose: resetMutationErrors,
          message: t('addToList.errorToListSave', { ns: 'common' }),
        });
        return;
      }

      try {
        await addToExisting([newItem]);
        setSuccessToast({
          message: (
            <>
              <Typography variant="headline-smallest">
                {t('addToList.toastLabel', { ns: 'common' })}
              </Typography>
              <Typography variant="body-copy-small">
                {t('addToList.listLabel', { ns: 'common' })}
              </Typography>
            </>
          ),
        });
        onClose();
      } catch (e) {
        setErrorToast({
          onClose: resetMutationErrors,
          message: t('addToList.errorToListSave', { ns: 'common' }),
        });
      }
    }
  };

  const handleAddNew = async () => {
    const newItem = buildNewItem();
    resetMutationErrors();

    if (typeof newItem === 'undefined') {
      setErrorToast({
        onClose: resetMutationErrors,
        message: t('addToList.errorToListSave', { ns: 'common' }),
      });
      return;
    }

    try {
      const createResponse = await createPlaylist({
        title: titleCounter.value,
        body: descriptionCounter.value,
        is_public: playlistIsPublic,
        site_language: currentLocale,
      });
      await addToExisting([
        {
          ...newItem,
          id: createResponse?.data?.id,
        },
      ]);
      onClose();

      setSuccessToast({
        message: (
          <>
            <Typography variant="headline-smallest">
              {t('addToList.toastLabel', { ns: 'common' })}
            </Typography>
            <Typography variant="body-copy-small">
              {t('addToList.toastNewLabel', { ns: 'common' })}
            </Typography>
          </>
        ),
      });
    } catch (e) {
      setErrorToast({
        onClose: resetMutationErrors,
        message: t('addToList.errorListCreate', { ns: 'common' }),
      });
    }
  };

  const inputStyles = {
    '& .MuiInputBase-input': {
      color: 'white !important',
      border: '1px solid white',
      '&::placeholder': {
        color: 'white !important',
        opacity: 1,
      },
    },
  };

  return (
    <Box py={4}>
      {!noExistingPlaylists && (
        <FormControl fullWidth>
          <InputLabel
            htmlFor="add-playlist-select"
            className="!au-text-white"
            sx={{ color: 'white' }}
          >
            {t('addToList.label_add_to_existing', { ns: 'common' })}
          </InputLabel>
          <div className="au-flex au-items-center au-gap-2">
            <ThemeProvider theme={createTheme(themeDark as ThemeOptions)}>
              <Select
                sx={{
                  flexGrow: 1,
                }}
                value={currentList?.id ?? ''}
                disabled={isLoading || isMutating}
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    sx: {
                      width: '550px',
                      maxWidth: '70vw',
                    },
                  },
                }}
                name="add-playlist-select"
                id="add-playlist-select"
                onChange={async (e: SelectChangeEvent) => {
                  setSelectedPlaylist(e.target.value);
                }}
                options={[
                  {
                    value: '',
                    children: (
                      <em>
                        {t('addToList.select_placeholder', { ns: 'common' })}
                      </em>
                    ),
                  },
                  ...(publicList.map(item => ({
                    value: item.id,
                    children: item.title,
                  })) || []),
                ]}
              />
            </ThemeProvider>
            {isLoading && <Spinner className="au-w-c20 au-shrink-0" />}
          </div>
        </FormControl>
      )}
      <Box mt={noExistingPlaylists ? 0 : 6}>
        {!isCreateMode && (
          <div className="au-flex au-flex-wrap au-justify-end au-gap-y-c8 au-gap-x-c16">
            <Button
              variant="text"
              onClick={() => {
                setIsCreateMode(true);
              }}
              className="!au-text-white !au-border-white"
            >
              {t('addToList.link_label_create_new', { ns: 'common' })}
            </Button>
            <Button
              disabled={isLoading || isMutating || !currentList}
              onClick={handleAddExisting}
              className="!au-bg-[#FEFF09] !au-text-black"
            >
              {t('addToList.button_add_to_existing', { ns: 'common' })}
            </Button>
          </div>
        )}

        {isCreateMode && (
          <>
            {!noExistingPlaylists && (
              <>
                <div className="au-flex au-justify-end au-gap-c16">
                  <Button
                    disabled={isLoading || isMutating || !currentList}
                    onClick={handleAddExisting}
                    className="!au-bg-[#FEFF09] !au-text-black"
                  >
                    {t('addToList.button_add_to_existing', { ns: 'common' })}
                  </Button>
                </div>
                <Divider sx={{ mt: 7, mb: 6 }} />
                <Typography
                  variant="headline-smaller"
                  sx={{ mb: 4, color: 'white' }}
                  component="h3"
                >
                  {t('addToList.title_create_new', { ns: 'common' })}
                </Typography>
              </>
            )}
            <FormControl fullWidth error={titleCounter.exceeded} sx={{ mb: 4 }}>
              <InputLabel
                htmlFor="create-playlist-title"
                sx={{
                  mb: 1,
                  color: 'white',
                  '&.Mui-focused': {
                    color: 'white !important',
                  },
                }}
              >
                {t('addToList.label_create_new_title', { ns: 'common' })}
              </InputLabel>
              <Input
                disabled={isMutating}
                name="create-playlist-title"
                id="create-playlist-title"
                value={titleCounter.value}
                onChange={titleCounter.handleChange}
                error={titleCounter.exceeded}
                sx={inputStyles}
              />
              <CharacterCounter count={titleCounter.count} maxLength={80} />
              <FormHelperText
                sx={{
                  color: 'white',
                  '& .MuiSvgIcon-root': {
                    color: 'white',
                  },
                }}
              >
                <>
                  {!!titleCounter.exceeded && (
                    <SvgIcon stitle="Error" className="au-box-content">
                      <ErrorIcon />
                    </SvgIcon>
                  )}
                  {t('addToList.helptext_title', { ns: 'common' })}
                </>
              </FormHelperText>
            </FormControl>
            <FormControl
              fullWidth
              error={descriptionCounter.exceeded}
              sx={{ mb: 4 }}
            >
              <InputLabel
                htmlFor="create-playlist-body"
                sx={{
                  mb: 1,
                  color: 'white',
                  '&.Mui-focused': {
                    color: 'white !important',
                  },
                }}
              >
                {t('addToList.label_create_new_description', { ns: 'common' })}
              </InputLabel>
              <Input
                disabled={isMutating}
                name="create-playlist-body"
                id="create-playlist-body"
                value={descriptionCounter.value}
                error={descriptionCounter.exceeded}
                onChange={descriptionCounter.handleChange}
                multiline
                rows={10}
                sx={inputStyles}
              />
              <CharacterCounter
                count={descriptionCounter.count}
                maxLength={500}
                sx={{
                  '& .DhigTextInput--count': {
                    color: 'white !important',
                  },
                }}
              />
              <FormHelperText
                sx={{
                  color: 'white',
                  '& .MuiSvgIcon-root': {
                    color: 'white',
                  },
                }}
              >
                <>
                  {!!descriptionCounter.exceeded && (
                    <SvgIcon title="Error" className="au-box-content">
                      <ErrorIcon />
                    </SvgIcon>
                  )}
                  {t('addToList.helptext_description', { ns: 'common' })}
                </>
              </FormHelperText>
            </FormControl>
            <FormControl sx={{ mb: 4 }}>
              <InputLabel
                id="create-playlist-is-public"
                sx={{
                  mb: 1,
                  color: 'white',
                  '&.Mui-focused': {
                    color: 'white !important',
                  },
                }}
              >
                {t('playlists.share_radio_label', { ns: 'common' })}
              </InputLabel>
              <RadioGroup
                aria-labelledby="create-playlist-is-public"
                name="create-playlist-is-public"
                value={playlistIsPublic ? 'everyone' : 'link_only'}
                onChange={async (event: React.ChangeEvent, value: string) => {
                  setPlaylistIsPublic(value === 'everyone');
                }}
                sx={{
                  gap: 4,
                  '& .MuiTypography-root': {
                    color: 'white',
                  },
                }}
              >
                <FormControlLabel
                  sx={{ alignItems: 'flex-start' }}
                  control={<Radio sx={{ my: 0 }} />}
                  value="link_only"
                  disabled={isMutating}
                  label={
                    <Typography
                      variant="body-copy-medium"
                      sx={{
                        color: 'white',
                      }}
                    >
                      <Trans i18nKey="playlists.shareLinkOnly" ns="common">
                        <strong>Only people with the link.</strong> Your
                        playlist will not come up in search results or on your
                        profile. Only those who know the link can view it, and
                        you can share the link with anyone, even those who are
                        not signed in.
                      </Trans>
                    </Typography>
                  }
                />
                <FormControlLabel
                  sx={{ alignItems: 'flex-start' }}
                  control={<Radio sx={{ my: 0 }} />}
                  value="everyone"
                  disabled={isMutating}
                  label={
                    <Typography
                      variant="body-copy-medium"
                      sx={{
                        color: 'white',
                      }}
                    >
                      <Trans i18nKey="playlists.shareEveryone" ns="common">
                        <strong>Anyone.</strong> Other website users can
                        discover your playlist on the AU website in places like
                        search results, your profile, or the home page.
                      </Trans>
                    </Typography>
                  }
                />
              </RadioGroup>
            </FormControl>
            <div className="au-flex au-flex-wrap au-justify-end au-gap-y-c8 au-gap-x-c16">
              <Button
                variant="text"
                onClick={() => {
                  setIsCreateMode(true);
                  onClose();
                }}
                className="!au-text-white !au-border-white"
              >
                {t('Cancel', { ns: 'common' })}
              </Button>
              <Button
                disabled={
                  titleCounter.exceeded ||
                  descriptionCounter.exceeded ||
                  titleCounter.value.trim().length === 0 ||
                  descriptionCounter.value.trim().length === 0 ||
                  isMutating
                }
                onClick={handleAddNew}
                className="!au-bg-[#FEFF09] !au-text-black"
              >
                {t('addToList.button_create_new', { ns: 'common' })}
              </Button>
            </div>
          </>
        )}
      </Box>
    </Box>
  );
}

export default AddToPlaylistForm;
